import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { scoreBand } from './score-band';

interface CircleMeterProps {
  /** 0–100 maturity percentage. */
  value: number;
  size?: number;
}

/**
 * Radial maturity gauge for the maturity hero. Band colour follows the shared
 * {@link scoreBand} thresholds. Direction-agnostic (the arc is centred), so it
 * needs no RTL-specific handling.
 */
export const CircleMeter: FC<CircleMeterProps> = observer(({ value, size = 176 }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(100, Math.max(0, value));
  const offset = circ * (1 - clamped / 100);
  const { color } = scoreBand(clamped, theme);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={theme.palette.grey[300]}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset .8s cubic-bezier(.4,0,.2,1), stroke .4s' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 44, fontWeight: 700, lineHeight: 1, color: 'text.primary' }}>
          {clamped}
          <Typography component="span" sx={{ fontSize: 20, color: 'text.tertiary' }}>
            %
          </Typography>
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: 'text.tertiary',
            mt: 0.5,
            textTransform: 'uppercase',
            letterSpacing: '.06em',
          }}
        >
          {t('app.dashboard.maturity.label')}
        </Typography>
      </Box>
    </Box>
  );
});
